/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useFocusVisible, useFocusWithin, useHover, useKeyboard } from 'react-aria';
import { type ContextValue, DEFAULT_SLOT, Provider, TextField as TextFieldCore, type TextFieldProps as TextFieldCoreProps, TooltipTriggerStateContext, useContextProps } from 'react-aria-components';
import { CheckmarkStrokeIcon } from '../../icons/checkmark-stroke/checkmark-stroke.icon';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { EditStrokeIcon } from '../../icons/edit-stroke/edit-stroke.icon';
import { MoreVerticalStrokeIcon } from '../../icons/more-vertical-stroke/more-vertical-stroke.icon';
import { $ } from '../../tokens.stylex';
import { HintContext } from '../hint/hint.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { IconContext } from '../icon/icon.component';
import { InputStyleContext } from '../input/input.component';
import { LabelContext } from '../label/label.component';
import { MenuItemContext } from '../menu-item/menu-item.component';
import { PopoverContext } from '../popover/popover.component';
import { SkeletonContext } from '../skeleton/skeleton.component';
import { TextAreaStyleContext } from '../text-area/text-area.component';
import { Text, TextContext } from '../text/text.component';
import { TooltipContext } from '../tooltip/tooltip.component';
import { View, ViewContext } from '../view/view.component';

interface InlineTableTextFieldProps extends Omit<TextFieldCoreProps, 'style' | 'className'> {
  style?: stylex.StyleXStyles;
  variant?: 'primary' | 'warning' | 'success' | 'error';
}

const InlineTableTextFieldContext = createContext<ContextValue<InlineTableTextFieldProps, HTMLDivElement>>({});

const InlineTableTextField = forwardRef((props: InlineTableTextFieldProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, InlineTableTextFieldContext);

  const { style, children, variant, onKeyDown, onKeyUp, onPointerEnter, onPointerLeave, ...coreProps } = props;

  const [isEditing, setIsEditing] = useState(coreProps.isReadOnly || false);
  const [isFocusWithin, setFocusWithin] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const exitHandledRef = useRef(false);

  const actionButtonRef = useCallback((node: HTMLButtonElement | null) => setIsEditable(!!node), []);
  const [hasPreview, setHasPreview] = useState(false);
  const previewRef = useCallback((node: HTMLElement | null) => setHasPreview(!!node), []);

  const inputRef = useRef<HTMLDivElement>(null);

  const { isFocusVisible } = useFocusVisible({});

  const { hoverProps, isHovered } = useHover({ isDisabled: coreProps.isDisabled });

  const { focusWithinProps } = useFocusWithin({
    isDisabled: coreProps.isDisabled,
    onFocusWithinChange: (isFocusWithin) => {
      setFocusWithin(isFocusWithin);
      if (!isFocusWithin && isEditing) {
        if (exitHandledRef.current) {
          exitHandledRef.current = false;
          setIsEditing(false);
          return;
        }
        setIsEditing(false);
        ref.current?.closest('form')?.requestSubmit();
      }
    },
  });

  const [isTextArea, setIsTextArea] = useState(false);
  useEffect(() => {
    setIsTextArea(!!ref.current?.querySelector('textarea'));
  }, []);

  let { keyboardProps } = useKeyboard({
    isDisabled: coreProps.isDisabled,
    onKeyDown: (e) => {
      e.continuePropagation();

      if (e.key === 'Escape') {
        e.preventDefault();
        exitHandledRef.current = true;
        setIsEditing(false);
        ref.current?.closest('form')?.reset();
      }
      if (e.key === 'Enter' && !isTextArea) {
        e.preventDefault();
        exitHandledRef.current = true;
        setIsEditing(false);
        ref.current?.closest('form')?.requestSubmit();
      }

      onKeyDown?.(e);
    },
    onKeyUp,
  });

  useEffect(() => {
    if (isEditing && ref.current) {
      const inputElement = ref.current.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
      if (inputElement) {
        inputElement.focus();
        inputElement.selectionStart = coreProps.value?.length || 0;

        requestAnimationFrame(() => inputElement.scrollLeft = inputElement.scrollWidth);
      }
    }
  }, [isEditing, coreProps.value?.length]);

  useEffect(() => {
    const inputElement = ref.current?.querySelector('input') as HTMLInputElement | null;

    const disableWheel = (e: WheelEvent) => {
      if (e.shiftKey) {
        e.preventDefault();
      }
    };

    if (inputElement && !isEditing) {
      inputElement.addEventListener('wheel', disableWheel, { passive: false });
    }

    return () => inputElement?.removeEventListener('wheel', disableWheel);
  }, [isEditing]);

  return (
    <View
      ref={ref}
      onFocus={focusWithinProps.onFocus}
      onBlur={focusWithinProps.onBlur}
      style={[styles.base, style]}>
      <TextFieldCore
        excludeFromTabOrder
        data-hovered={isHovered || undefined}
        data-editing={isEditing || undefined}
        onPointerEnter={(e) => {
          hoverProps.onPointerEnter?.(e);
          onPointerEnter?.(e);
        }}
        onPointerLeave={(e) => {
          hoverProps.onPointerLeave?.(e);
          onPointerLeave?.(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' || (e.key === 'Enter' && !isTextArea)) {
            e.preventDefault();
          }
          keyboardProps.onKeyDown?.(e);
        }}
        onKeyUp={keyboardProps.onKeyUp}
        {...stylex.props(
          textFieldStyles.base,
          variant && textFieldStyles[variant],
          isEditing && textFieldStyles.editing,
          isEditing && coreProps.isInvalid && textFieldStyles.invalid)}
        {...coreProps}
        isReadOnly={!isEditing || coreProps.isReadOnly}>
        {(renderProps) => (
          <Provider values={[
            [LabelContext, { color: isHovered || isEditing || (isFocusWithin && isFocusVisible) ? 'primary' : 'tertiary', size: 14, style: labelStyles.base }],
            [InputStyleContext, {
              ref: inputRef,
              size: 32,
              style: {
                base: [
                  inputStyles.base,
                  !isEditing && inputStyles.readOnly,
                  isEditable && !isEditing && inputStyles.padded,
                  hasPreview && !isEditing && inputStyles.hidden,
                ],
                input: [inputStyles.input],
              },
            }],
            [TextAreaStyleContext, {
              ref: inputRef,
              size: 32,
              style: {
                base: [
                  textAreaStyles.base,
                  !isEditing && textAreaStyles.readOnly,
                  isEditable && !isEditing && textAreaStyles.padded,
                  hasPreview && !isEditing && textAreaStyles.hidden,
                ],
                textArea: [textAreaStyles.textArea],
              },
            }],
            [HintContext, { style: [hintStyles.base, !isEditing && coreProps.isInvalid && hintStyles.hidden] }],
            [TooltipContext, {
              isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
              triggerRef: inputRef as React.RefObject<Element>,
            }],
            [TooltipTriggerStateContext, {
              isOpen: (isHovered || (isFocusWithin && isFocusVisible)) && !isEditing,
              open() { /* controlled by isHovered */ },
              close() { /* controlled by isHovered */ },
            }],
            [PopoverContext, {
              placement: "bottom right",
              offset: 5
            }],
            [IconButtonContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_TEXT_FIELD_SLOTS.ACTION_BUTTON]: {
                  ref: actionButtonRef,
                  variant: 'ghostLowContrast',
                  size: 32,
                  "aria-label": "Actions",
                  hidden: isEditing,
                  style: [iconButtonStyles.actionButton, isEditing && iconButtonStyles.hidden],
                  children: <MoreVerticalStrokeIcon />
                }
              }
            }],
            [MenuItemContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_TEXT_FIELD_SLOTS.ACTION_MENU_ITEM]: {
                  onAction: () => setIsEditing(true),
                  children: (
                    <>
                      <EditStrokeIcon />
                      <Text>Edit</Text>
                    </>
                  )
                },
              },
            }],
            [SkeletonContext, { style: skeletonStyles.base }],
            [ViewContext, {
              slots: {
                [DEFAULT_SLOT]: {},
                [INLINE_TABLE_TEXT_FIELD_SLOTS.PREVIEW]: {
                  ref: previewRef,
                  "aria-hidden": true,
                  style: [previewStyles.base, isEditable && previewStyles.padded, isEditing && previewStyles.hidden],
                }
              }
            }],
          ]}>
            <Provider values={[
              [TextContext, {
                slot: null!,
                size: 14,
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {},
                  [INLINE_TABLE_TEXT_FIELD_SLOTS.INDICATOR_ICON]: {
                    "aria-hidden": true,
                    size: 16,
                    style: indicatorStyles.base,
                    ...(variant && variant !== 'primary' && { color: VARIANT_INDICATOR_COLOR[variant] }),
                    ...(variant && variant !== 'primary' && { children: VARIANT_INDICATOR_ICON[variant] }),
                  }
                }
              }],
            ]}>
              {typeof children === 'function' ? children?.(renderProps) : children}
            </Provider>
          </Provider>
        )}
      </TextFieldCore>
    </View>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 6,
    gridColumn: '1 / -1',
    alignItems: 'center',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
  },
});

const textFieldStyles = stylex.create({
  base: {
    borderRadius: 4,
    gridColumn: '1 / -1',
    alignItems: 'flex-start',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    position: 'relative',
    minHeight: 36,
  },
  primary: {},
  warning: {
    backgroundColor: $.surfaceAttentiveSilent,
  },
  success: {
    backgroundColor: $.surfacePositiveSilent,
  },
  error: {
    backgroundColor: $.surfaceCriticalSilent,
  },
  editing: {
    backgroundColor: $.surfaceHighlightSilent,
  },
  invalid: {
    backgroundColor: $.surfaceCriticalSilent,
  }
});

const labelStyles = stylex.create({
  base: {
    paddingInline: 8,
    alignItems: 'flex-start',
    transitionDuration: '0.2s',
    transitionProperty: 'color',
    transitionTimingFunction: 'easy-in-out',
    height: 36,
    paddingTop: 10,
  }
});

const inputStyles = stylex.create({
  base: {
    borderStyle: 'none',
    backgroundColor: null,
    cursor: null,
    height: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
  },
  input: {
    color: $.textPrimary,
  },
  readOnly: {
    pointerEvents: 'none',
  },
});

const textAreaStyles = stylex.create({
  base: {
    borderStyle: 'none',
    overflow: 'visible',
    backgroundColor: null,
    cursor: null,
    minHeight: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
  },
  textArea: {
    color: $.textPrimary,
  },
  readOnly: {
    pointerEvents: 'none',
  },
});

const iconButtonStyles = stylex.create({
  actionButton: {
    position: 'absolute',
    right: 2,
    top: 2,
  },
  hidden: {
    display: 'none',
  },
});

const hintStyles = stylex.create({
  base: {
    gridColumn: '2',
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
  },
  hidden: {
    display: 'none',
  }
});

const previewStyles = stylex.create({
  base: {
    gap: 8,
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minHeight: 36,
    minWidth: 0,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 6,
  },
  padded: {
    paddingRight: 40,
  },
  hidden: {
    display: 'none',
  },
});

const skeletonStyles = stylex.create({
  base: {
    marginInline: 8,
    width: 'auto',
  },
});

const indicatorStyles = stylex.create({
  base: {
    marginLeft: 'auto',
    marginRight: 8,
  },
});

const VARIANT_INDICATOR_ICON = {
  warning: <EditStrokeIcon />,
  success: <CheckmarkStrokeIcon />,
  error: <CloseStrokeIcon />,
};

const VARIANT_INDICATOR_COLOR = {
  warning: 'attentive',
  success: 'positive',
  error: 'destructive',
} as const;

const INLINE_TABLE_TEXT_FIELD_SLOTS = {
  PREVIEW: 'preview',
  INDICATOR_ICON: 'indicator',
  ACTION_BUTTON: 'action-button',
  ACTION_MENU_ITEM: 'action-menu-item',
};

export { INLINE_TABLE_TEXT_FIELD_SLOTS, InlineTableTextField, InlineTableTextFieldContext, type InlineTableTextFieldProps };
